// src/pages/notifications/TemplateListPage.tsx
// Notification templates list with filters.

import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useNotificationsTemplate, useI18n } from '@react/hooks';
import { Button, Input, Card, CardHeader, CardContent, Badge, ErrorAlert,
} from '@react/components';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableEmpty } from '@react/components';
import { formatChannelType } from '../../features/notifications/logic';
import { NotificationIcon } from '../../components/NotificationIcon';
import { NotificationChannelTypeValues } from '@core/types/notifications/NotificationChannelType';
import type { NotificationTemplate } from '@core/types/notifications/NotificationTemplate';
import { Select } from '@/components/Select';

export const TemplateListPage = observer(() => {
  const store = useNotificationsTemplate();
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('q') ?? '');
  const [channelFilter, setChannelFilter] = useState(searchParams.get('channel') ?? '');
  const [langFilter, setLangFilter] = useState(searchParams.get('lang') ?? '');

  useEffect(() => {
    store.ListNotificationTemplates({ limit: 20 } as never);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params: Record<string, string> = {};
    if (search) params.q = search;
    if (channelFilter) params.channel = channelFilter;
    if (langFilter) params.lang = langFilter;
    setSearchParams(params);
    store.ListNotificationTemplates({ limit: 20, search, channel_type: channelFilter, language: langFilter } as never);
  };

  const handleDelete = async (id: string) => {
    if (!confirm(t('notifications.deleteTemplateConfirm'))) return;
    try {
      await store.DeleteTemplate(id);
    } catch {
      // handled by store
    }
  };

  const items = store.items as NotificationTemplate[];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">{t('notifications.templates')}</h2>
        <Link to="/notifications/templates/new"><Button>{t('notifications.createTemplate')}</Button></Link>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <Card>
        <CardHeader>
          <form onSubmit={handleSearch} className="flex flex-wrap gap-2">
            <Input placeholder={t('common.search')} value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-xs" />
            <Select value={channelFilter} onChange={(e) => setChannelFilter(e.target.value)} fullWidth={false}>
              <option value="">{t('common.all')} {t('notifications.channelType')}</option>
              {NotificationChannelTypeValues.filter(v => v !== 'NOTIFICATION_CHANNEL_TYPE_UNSPECIFIED').map((s) => {
                const ct = formatChannelType(s);
                return <option key={s} value={s}>{t(ct.labelKey)}</option>;
              })}
            </Select>
            <Input placeholder={t('notifications.language')} value={langFilter} onChange={(e) => setLangFilter(e.target.value)} className="max-w-[120px]" />
            <Button type="submit" variant="secondary">{t('common.search')}</Button>
          </form>
        </CardHeader>
        <CardContent>
          <Table hoverable>
            <TableHeader>
              <TableRow>
                <TableHead>{t('common.name')}</TableHead>
                <TableHead>{t('notifications.channelType')}</TableHead>
                <TableHead>{t('notifications.eventType')}</TableHead>
                <TableHead>{t('notifications.language')}</TableHead>
                <TableHead>{t('notifications.active')}</TableHead>
                <TableHead>{t('notifications.version')}</TableHead>
                <TableHead>{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 && !store.isLoading && <TableEmpty colSpan={7} message={t('notifications.noTemplates')} />}
              {items.map((tpl) => {
                const chType = formatChannelType(tpl.channel_type ?? '');
                return (
                  <TableRow key={tpl.entity?.id}>
                    <TableCell>
                      <Link to={`/notifications/templates/${tpl.entity?.id}`} className="text-sm font-medium text-primary hover:text-primary/80">{tpl.name}</Link>
                    </TableCell>
                    <TableCell className="text-sm"><span className="inline-flex items-center gap-1.5"><NotificationIcon name={chType.icon} className="h-4 w-4" />{t(chType.labelKey)}</span></TableCell>
                    <TableCell className="text-sm">{tpl.event_type || '-'}</TableCell>
                    <TableCell className="text-sm">{tpl.language}</TableCell>
                    <TableCell><Badge variant={tpl.is_active ? 'success' : 'secondary'}>{tpl.is_active ? t('common.yes') : t('common.no')}</Badge></TableCell>
                    <TableCell className="text-sm">v{tpl.version}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Link to={`/notifications/templates/${tpl.entity?.id}`}><Button variant="ghost" size="sm">{t('common.open')}</Button></Link>
                        <Link to={`/notifications/templates/${tpl.entity?.id}/edit`}><Button variant="ghost" size="sm">{t('common.edit')}</Button></Link>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(tpl.entity?.id ?? '')}>{t('common.delete')}</Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {store.hasMore && (
            <div className="mt-4 flex justify-between items-center">
              <span className="text-sm text-muted-foreground">{t('common.showingItems', { count: items.length })}</span>
              <Button variant="secondary" size="sm" onClick={() => store.ListNotificationTemplates({ limit: 20, cursor: store.pageInfo?.next_cursor } as never)} isLoading={store.isLoading}>{t('common.next')}</Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
});

export default TemplateListPage;
